import { money } from "@/components/StatusBadge";
import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Receipt, Download, Upload, Send, UserPlus, Trash2, Eraser } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";

const PurgeSimulatedButton = ({ onDone }) => {
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/simulated-data/purge", { confirm: "DELETE" });
      toast.success(`Cleared ${data.wallet_transactions_deleted} transactions and reset ${data.wallets_reset} wallets`);
      setOpen(false);
      setConfirmText("");
      onDone && onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to purge simulated data");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setConfirmText(""); }}>
      <DialogTrigger asChild>
        <Button data-testid="purge-simulated-btn" variant="outline" size="sm"
          className="rounded-full border-destructive/40 text-destructive hover:bg-destructive/10">
          <Eraser className="mr-1.5 h-4 w-4" /> Remove simulated data
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="purge-simulated-dialog">
        <DialogHeader>
          <DialogTitle>Remove all simulated demo money?</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          This zeroes every wallet &amp; promo balance, deletes the wallet transaction ledger, clears
          simulated payout state, and removes booked revenue. Do this to clean the books before enabling a
          real payout rail. <b className="text-destructive">This cannot be undone.</b>
        </p>
        <div>
          <Label htmlFor="purge-confirm" className="text-xs">Type <b>DELETE</b> to confirm</Label>
          <Input id="purge-confirm" data-testid="purge-confirm-input" value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)} placeholder="DELETE" className="mt-1" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} data-testid="purge-cancel-btn">Cancel</Button>
          <Button variant="destructive" data-testid="purge-confirm-btn"
            disabled={busy || confirmText !== "DELETE"} onClick={run}>
            {busy ? "Clearing…" : "Remove simulated data"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Wallet summary + revenue report + credit transfer + user management.
// All state/handlers come from AdminDashboard (kept there because they are shared).
export const WalletsTab = ({
  walletSummary, revenue, revenueMonthly, rangeStart, rangeEnd, setRangeStart, setRangeEnd,
  loadWalletSummary, exportCsv, bulkCredit, downloadCreditTemplate, exportWalletActivity,
  creditAmount, setCreditAmount, creditNote, setCreditNote, crediting, creditWallets,
  userQuery, setUserQuery, loadUsers, users, selectedUsers, toggleUser, toggleAllUsers, bulkDeleteUsers,
  userDialogOpen, setUserDialogOpen, newUser, setNewUser, createUser, creatingUser,
  openUserDetail, removeUser,
}) => (
  <>
    {walletSummary && (
      <div data-testid="wallet-summary" className="mb-5 rounded-xl border border-black/10 bg-white p-5">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h3 className="font-heading text-lg" style={{ fontWeight: 700 }}>Wallet summary</h3>
            <p className="text-xs text-muted-foreground">Reconciliation totals for the selected date range.</p>
          </div>
          <div className="flex items-end gap-2">
            <PurgeSimulatedButton onDone={() => loadWalletSummary(rangeStart, rangeEnd)} />
            <div>
              <Label htmlFor="range-start" className="text-[11px]">From</Label>
              <Input id="range-start" data-testid="range-start-input" type="date" value={rangeStart}
                onChange={(e) => { setRangeStart(e.target.value); loadWalletSummary(e.target.value, rangeEnd); }}
                className="mt-1 h-9 w-[150px]" />
            </div>
            <div>
              <Label htmlFor="range-end" className="text-[11px]">To</Label>
              <Input id="range-end" data-testid="range-end-input" type="date" value={rangeEnd}
                onChange={(e) => { setRangeEnd(e.target.value); loadWalletSummary(rangeStart, e.target.value); }}
                className="mt-1 h-9 w-[150px]" />
            </div>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {[
            ["Credited in", walletSummary.total_credited, "bg-green-100 text-green-700", "sum-credited"],
            ["Fees paid", walletSummary.fee_payments, "bg-blue-100 text-blue-700", "sum-fees"],
            ["Admin fees earned", walletSummary.admin_fees_collected, "bg-accent/15 text-accent", "sum-admin-fees"],
            ["Withdrawn", walletSummary.total_withdrawn, "bg-secondary text-foreground", "sum-withdrawn"],
            ["Payouts held", walletSummary.payouts_held, "bg-amber-100 text-amber-700", "sum-held"],
            ["Total balance owed", walletSummary.total_wallet_liability, "bg-foreground/10 text-foreground", "sum-liability"],
          ].map(([label, val, cls, tid]) => (
            <div key={tid} className="rounded-lg border border-black/5 p-3">
              <p className="text-[11px] text-muted-foreground">{label}</p>
              <p data-testid={tid} className={`mt-0.5 inline-block rounded px-1.5 font-heading text-lg ${cls}`} style={{ fontWeight: 700 }}>{money(val)}</p>
            </div>
          ))}
        </div>
      </div>
    )}
    {revenue && (
      <div data-testid="revenue-report" className="mb-5 rounded-xl border border-black/10 bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent/10 text-accent"><Receipt className="h-4 w-4" /></div>
              <h3 className="font-heading text-lg" style={{ fontWeight: 700 }}>Revenue report</h3>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">Admin fees earned from completed splits in the selected date range (same From/To above).</p>
          </div>
          <Button variant="outline" size="sm" data-testid="export-revenue-btn"
            onClick={() => exportCsv(`/admin/revenue/export?start=${rangeStart}&end=${rangeEnd}`, `bogoshare_revenue_${rangeStart}_${rangeEnd}.csv`)}
            className="rounded-full border-black/20">
            <Download className="mr-1 h-4 w-4" /> Download CSV
          </Button>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-3">
          <div className="rounded-lg border border-black/5 p-3">
            <p className="text-[11px] text-muted-foreground">Total admin fees</p>
            <p data-testid="revenue-total" className="mt-0.5 inline-block rounded bg-accent/15 px-1.5 font-heading text-lg text-accent" style={{ fontWeight: 700 }}>{money(revenue.total_admin_fees)}</p>
          </div>
          <div className="rounded-lg border border-black/5 p-3">
            <p className="text-[11px] text-muted-foreground">Completed splits</p>
            <p data-testid="revenue-count" className="mt-0.5 font-heading text-lg" style={{ fontWeight: 700 }}>{revenue.count}</p>
          </div>
          <div className="rounded-lg border border-black/5 p-3">
            <p className="text-[11px] text-muted-foreground">Average fee</p>
            <p data-testid="revenue-average" className="mt-0.5 font-heading text-lg" style={{ fontWeight: 700 }}>{money(revenue.average_fee)}</p>
          </div>
        </div>
        {revenueMonthly && revenueMonthly.series?.length > 0 && (
          <div className="mt-5" data-testid="revenue-chart">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-foreground/70">Admin-fee revenue · last 6 months</p>
              <p className="text-[11px] text-muted-foreground">6-mo total {money(revenueMonthly.total)}</p>
            </div>
            <div className="mt-2 h-44 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={revenueMonthly.series} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#00000010" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={{ stroke: "#00000015" }} />
                  <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={48}
                    tickFormatter={(v) => `$${v}`} />
                  <Tooltip
                    cursor={{ fill: "#00000008" }}
                    formatter={(v, _n, p) => [`${money(v)} · ${p.payload.count} split${p.payload.count === 1 ? "" : "s"}`, "Admin fees"]}
                    contentStyle={{ borderRadius: 10, border: "1px solid #00000015", fontSize: 12 }}
                  />
                  <Bar dataKey="total" fill="#16a34a" radius={[6, 6, 0, 0]} maxBarSize={48} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    )}
    <div className="rounded-xl border border-black/10 bg-white p-5">
      <h3 className="font-heading text-lg" style={{ fontWeight: 700 }}>Transfer money into wallets</h3>
      <p className="mt-1 text-xs text-muted-foreground">Select users and credit their BogoShare wallet balance (e.g. bonuses, refunds, adjustments).</p>

      <div className="mt-3 flex items-center gap-2">
        <input type="file" accept=".csv" id="bulk-credit-input" data-testid="bulk-credit-input" onChange={bulkCredit} className="hidden" />
        <Button variant="outline" size="sm" data-testid="bulk-credit-btn" onClick={() => document.getElementById("bulk-credit-input").click()} className="rounded-full border-black/20">
          <Upload className="mr-1 h-4 w-4" /> Bulk credit CSV
        </Button>
        <Button variant="ghost" size="sm" data-testid="credit-template-btn" onClick={downloadCreditTemplate} className="text-muted-foreground">
          <Download className="mr-1 h-4 w-4" /> Template
        </Button>
        <Button variant="ghost" size="sm" data-testid="export-wallet-activity-btn" onClick={exportWalletActivity} className="text-muted-foreground">
          <Download className="mr-1 h-4 w-4" /> Export activity
        </Button>
      </div>

      <div className="mt-4 flex flex-col sm:flex-row gap-3 sm:items-end">
        <div className="flex-1">
          <Label htmlFor="credit-amount" className="text-xs">Amount per user (CAD)</Label>
          <Input id="credit-amount" data-testid="credit-amount-input" type="number" min="1" value={creditAmount}
            onChange={(e) => setCreditAmount(e.target.value)} placeholder="25.00" className="mt-1" />
        </div>
        <div className="flex-1">
          <Label htmlFor="credit-note" className="text-xs">Note (optional)</Label>
          <Input id="credit-note" data-testid="credit-note-input" value={creditNote}
            onChange={(e) => setCreditNote(e.target.value)} placeholder="Welcome bonus" className="mt-1" />
        </div>
        <Button data-testid="transfer-credit-btn" onClick={creditWallets} disabled={crediting} className="rounded-full">
          <Send className="mr-1.5 h-4 w-4" />
          {crediting ? "Transferring…" : `Transfer${selectedUsers.length ? ` to ${selectedUsers.length}` : ""}`}
        </Button>
      </div>

      <div className="mt-5 flex items-center gap-3">
        <Input
          data-testid="user-search-input"
          value={userQuery}
          onChange={(e) => { setUserQuery(e.target.value); loadUsers(e.target.value); }}
          placeholder="Search users by name or email…"
          className="max-w-sm"
        />
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
          <Checkbox data-testid="users-select-all"
            checked={users.length > 0 && selectedUsers.length === users.length}
            onCheckedChange={toggleAllUsers} aria-label="Select all users" />
          Select all
        </label>
        <span className="text-xs text-muted-foreground">{selectedUsers.length} selected · {users.length} shown</span>
        {selectedUsers.length > 0 && (
          <Button size="sm" variant="destructive" data-testid="users-bulk-delete"
            onClick={bulkDeleteUsers} className="h-8 rounded-full">
            <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete selected
          </Button>
        )}
        <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-btn" size="sm" className="ml-auto rounded-full">
              <UserPlus className="mr-1.5 h-4 w-4" /> Add user
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="add-user-dialog">
            <DialogHeader><DialogTitle>Add a new user</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs">Full name</Label>
                <Input data-testid="new-user-name" value={newUser.name}
                  onChange={(e) => setNewUser((p) => ({ ...p, name: e.target.value }))} className="mt-1" /></div>
              <div><Label className="text-xs">Email</Label>
                <Input data-testid="new-user-email" type="email" value={newUser.email}
                  onChange={(e) => setNewUser((p) => ({ ...p, email: e.target.value }))} className="mt-1" /></div>
              <div><Label className="text-xs">Account setup</Label>
                <div className="mt-1 grid grid-cols-2 gap-2">
                  <button type="button" data-testid="new-user-mode-invite"
                    onClick={() => setNewUser((p) => ({ ...p, send_invite: true }))}
                    className={`rounded-md border px-3 py-2 text-sm transition-colors ${newUser.send_invite ? "border-black bg-black text-white" : "border-input bg-background hover:bg-accent"}`}>
                    Email invite link
                  </button>
                  <button type="button" data-testid="new-user-mode-password"
                    onClick={() => setNewUser((p) => ({ ...p, send_invite: false }))}
                    className={`rounded-md border px-3 py-2 text-sm transition-colors ${!newUser.send_invite ? "border-black bg-black text-white" : "border-input bg-background hover:bg-accent"}`}>
                    Set temp password
                  </button>
                </div>
              </div>
              {newUser.send_invite ? (
                <p data-testid="new-user-invite-note" className="text-xs text-muted-foreground">
                  The user gets an email with a secure link (valid 72 hours) to set their own password. No temporary password is shared.
                </p>
              ) : (
                <div><Label className="text-xs">Temporary password (6+ chars)</Label>
                  <Input data-testid="new-user-password" value={newUser.password}
                    onChange={(e) => setNewUser((p) => ({ ...p, password: e.target.value }))} className="mt-1" /></div>
              )}
              <div><Label className="text-xs">Role</Label>
                <select data-testid="new-user-role" value={newUser.role}
                  onChange={(e) => setNewUser((p) => ({ ...p, role: e.target.value }))}
                  className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select></div>
            </div>
            <DialogFooter>
              <Button data-testid="create-user-btn" onClick={createUser} disabled={creatingUser} className="rounded-full">
                {creatingUser ? "Creating…" : "Create user"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-3 divide-y divide-black/5 rounded-lg border border-black/10 max-h-[420px] overflow-y-auto">
        {users.length === 0 ? (
          <p className="p-4 text-sm text-muted-foreground">No users found.</p>
        ) : (
          users.map((u) => (
            <div
              key={u.user_id}
              data-testid={`user-row-${u.user_id}`}
              className="flex items-center gap-3 p-3 hover:bg-secondary/50"
            >
              <Checkbox
                data-testid={`user-check-${u.user_id}`}
                checked={selectedUsers.includes(u.user_id)}
                onCheckedChange={() => toggleUser(u.user_id)}
              />
              <div className="flex-1 min-w-0">
                <button
                  data-testid={`user-open-${u.user_id}`}
                  onClick={() => openUserDetail(u)}
                  className="text-left w-full group"
                >
                  <p className="text-sm font-medium truncate group-hover:text-primary group-hover:underline">
                    {u.name || "—"}
                    {u.role === "admin" && <span className="ml-2 text-[10px] font-semibold text-primary">ADMIN</span>}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                </button>
              </div>
              <span data-testid={`user-balance-${u.user_id}`} className="text-sm font-heading shrink-0" style={{ fontWeight: 700 }}>
                {money(u.wallet_balance)}
              </span>
              {!u.is_seed_admin && (
                <button
                  data-testid={`remove-user-${u.user_id}`}
                  onClick={() => removeUser(u)}
                  title="Remove user"
                  className="shrink-0 rounded-md p-1.5 text-muted-foreground hover:bg-red-50 hover:text-red-600 transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  </>
);

export default WalletsTab;
